import type { SelectHTMLAttributes } from 'react';
import { cx } from '@/lib/cx';
import { ChevronDownIcon } from './icons';
import styles from './Select.module.css';

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps
  // `size` on a native select means 'how many rows to show'. Ours is the shared
  // control scale, so the native attribute is dropped rather than shadowed.
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className' | 'children' | 'size'> {
  options: readonly SelectOption[];
  /** The label for the empty choice. Omit to make the select mandatory. */
  placeholder?: string;
  size?: 'sm' | 'md';
  /** Marks the control as narrowing the result set. */
  active?: boolean;
}

/**
 * A native select.
 *
 * Native on purpose: it inherits keyboard handling, the OS touch picker and its own
 * scrolling. A custom listbox has to re-earn all three, and a table's filter row is
 * the wrong place to spend that budget.
 *
 * The chevron is the shared icon, OVERLAID: nothing can be rendered inside a native
 * select, so it sits on top with clicks passing through. It replaced a chevron
 * painted in CSS gradients, which could never match the drawn icon the custom
 * dropdown triggers use — same control family, two different arrows.
 */
export const Select = ({ options, placeholder, size = 'sm', active = false, ...rest }: SelectProps) => (
  <span className={cx(styles.wrap, active && styles.wrapActive)}>
    <select className={cx(styles.select, size === 'md' && styles.md, active && styles.active)} {...rest}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    <ChevronDownIcon className={styles.chevron} />
  </span>
);
